<template>
  <div class="asc-report-column-table">
    <table
      :class="[
        report.borders
          ? 'asc-report-column-table--borders'
          : 'asc-report-column-table--no-borders',
      ]"
    >
      <thead v-if="hasTableHeadings" class="asc-report-column-table--thead">
        <template v-if="hasTableSingleHeading">
          <tr class="top-header asc-report-column-table--thead-tr">
            <template v-for="(header, rIndex) in groupedHeadings">
              <th
                v-if="header.title.length == 0"
                :key="rIndex + header.title"
                scope="col"
                class="empty-header asc-report-column-table--thead-th"
                :colspan="header.count"
              >
                {{ header.title }}
              </th>
              <th
                v-if="header.title.length > 0"
                :key="rIndex + header.title"
                scope="col"
                class="asc-report-column-table--thead-th"
                :colspan="header.count"
              >
                {{ header.title }}
              </th>
            </template>
          </tr>
        </template>
        <template v-if="hasTableDoubleHeadings">
          <tr class="top-header asc-report-column-table--thead-tr">
            <template v-for="(header, rIndex) in groupedTopHeadings">
              <th
                v-if="header.title.length == 0"
                :key="rIndex + header.title"
                scope="col"
                class="empty-header asc-report-column-table--thead-th"
                :colspan="header.count"
              >
                {{ header.title }}
              </th>
              <th
                v-if="header.title.length > 0"
                :key="rIndex + header.title"
                scope="col"
                class="asc-report-column-table--thead-th"
                :colspan="header.count"
              >
                {{ header.title }}
              </th>
            </template>
          </tr>
          <tr class="bottom-header asc-report-column-table--thead-tr">
            <template v-for="(header, rIndex) in groupedHeadings">
              <th
                v-if="header.title.length == 0"
                :key="rIndex + header.title"
                scope="col"
                class="empty-header asc-report-column-table--thead-th"
                :colspan="header.count"
              >
                {{ header.title }}
              </th>
              <th
                v-if="header.title.length > 0"
                :key="rIndex"
                class="asc-report-column-table--thead-th"
                scope="col"
                :colspan="header.count"
              >
                {{ header.title }}
              </th>
            </template>
          </tr>
        </template>
      </thead>
      <tbody v-if="hasTableData" class="asc-report-column-table--tbody">
        <tr
          v-for="(row, rIndex) in report.values"
          :key="rIndex"
          class="asc-report-column-table--tbody-tr"
        >
          <template v-for="(cell, cIndex) in row">
            <th
              v-if="cell.header"
              :key="cIndex + rIndex"
              class="asc-report-column-table--tbody-th"
            >
              {{ cell.text }}
            </th>
            <td
              v-if="!cell.header && !cell.parameterId && !cell.container"
              :key="cIndex + rIndex"
              :class="[
                {
                  'asc-report-column-table--tbody-td': true,
                  'table-cell-borders': report.borders,
                  'table-cell-no-borders': report.borders === false,
                  'table-cell--padding-right': cell.paddingRight,
                  'even-row':
                    report.evenOddRows !== false && (rIndex + 1) % 2 === 0,
                  'table-cell--md-font-size': hasNoHeadingsNoBorders,
                },
              ]"
              :colspan="cell.mergeCount ? cell.mergeCount : 1"
            >
              <div class="table-cell--simple-text">{{ cell.text }}</div>
            </td>
            <td
              v-if="!cell.header && cell.parameterId && !cell.container"
              :key="cIndex + rIndex"
              :class="[
                {
                  'asc-report-column-table--tbody-td': true,
                  'table-cell--hover': true,
                  'table-cell--drill-ripple': true,
                  'table-cell--drill-ripple-accent': isProjectZeus,
                  'table-subtotal': cell.subtotal === true,
                  'table-cell--center': cell.mergeCount === true,
                  'table-cell-borders': report.borders,
                  'table-cell-no-borders': report.borders === false,
                  'table-cell--padding-right': cell.paddingRight,
                  'even-row':
                    report.evenOddRows !== false && (rIndex + 1) % 2 === 0,
                  'table-cell--md-font-size': hasNoHeadingsNoBorders,
                  'table-cell--hover-warning': isWarningAlert(cell),
                  'table-cell--warning-drill-ripple': isWarningAlert(cell),
                  'table-cell--hover-caution': isCautionAlert(cell),
                  'table-cell--caution-drill-ripple': isCautionAlert(cell),
                  'table-cell--hover-fault-monitor': isFaultMonitorAlert(cell),
                  'table-cell--fault-monitor-drill-ripple':
                    isFaultMonitorAlert(cell),
                },
              ]"
              :colspan="cell.mergeCount ? cell.mergeCount : 1"
            >
              <asc-report-table-cell
                :report-cell="cell"
                :no-margins="isNoMarginsTableRow(row)"
                :on-report-menu-click="onReportMenuClick"
              ></asc-report-table-cell>
            </td>
            <td
              v-if="cell.container"
              :key="rIndex + cIndex"
              :class="[
                {
                  'asc-report-column-table--tbody-td': true,
                  'table-subtotal': cell.subtotal === true,
                  'table-cell--center': cell.mergeCount === true,
                  'table-cell-borders': report.borders,
                  'table-cell-no-borders': report.borders === false,
                  'table-cell--padding-right': cell.paddingRight,
                  'even-row':
                    report.evenOddRows !== false && (rIndex + 1) % 2 === 0,
                  'table-cell--md-font-size': hasNoHeadingsNoBorders,
                },
              ]"
            >
              <table class="asc-report-column-table--embed">
                <tbody class="asc-report-column-table--embed-tbody">
                  <div
                    :class="[
                      { 'table-cell--padding-right': cell.paddingRight },
                    ]"
                  >
                    <template v-for="(childCell, j) in cell.values">
                      <td
                        v-if="
                          !childCell.header &&
                          !childCell.parameterId &&
                          !childCell.container
                        "
                        :key="j + rIndex"
                        :class="[
                          {
                            'asc-report-column-table--embed-td': true,
                            'table-cell-borders': report.borders,
                            'table-cell-no-borders': report.borders === false,
                            'table-cell--padding-right': childCell.paddingRight,
                            'even-row':
                              report.evenOddRows !== false &&
                              (rIndex + 1) % 2 === 0,
                            'table-cell--md-font-size': hasNoHeadingsNoBorders,
                          },
                        ]"
                        :colspan="cell.mergeCount ? childCell.mergeCount : 1"
                      >
                        <div class="table-cell--simple-text">
                          {{ childCell.text }}
                        </div>
                      </td>
                      <td
                        v-if="
                          !childCell.header &&
                          childCell.parameterId &&
                          !childCell.container
                        "
                        :key="j + rIndex"
                        :class="[
                          {
                            'asc-report-column-table--embed-td': true,
                            'table-cell--hover': true,
                            'table-cell--drill-ripple': !isProjectZeus,
                            'table-cell--drill-ripple-accent': isProjectZeus,
                            'table-cell-borders': report.borders,
                            'table-subtotal': childCell.subtotal === true,
                            'table-cell--center': childCell.mergeCount === true,
                            'table-cell-no-borders': report.borders === false,
                            'table-cell--padding-right': childCell.paddingRight,
                            'even-row':
                              report.evenOddRows !== false &&
                              (rIndex + 1) % 2 === 0,
                            'table-cell--md-font-size': hasNoHeadingsNoBorders,
                            'table-cell--hover-warning':
                              isWarningAlert(childCell),
                            'table-cell--warning-drill-ripple':
                              isWarningAlert(childCell),
                            'table-cell--hover-caution':
                              isCautionAlert(childCell),
                            'table-cell--caution-drill-ripple':
                              isCautionAlert(childCell),
                            'table-cell--hover-fault-monitor':
                              isFaultMonitorAlert(childCell),
                            'table-cell--fault-monitor-drill-ripple':
                              isFaultMonitorAlert(childCell),
                          },
                        ]"
                        :colspan="
                          childCell.mergeCount ? childCell.mergeCount : 1
                        "
                      >
                        <asc-report-table-cell
                          :report-cell="childCell"
                          :no-margins="isNoMarginsTableRow(row)"
                          :on-report-menu-click="onReportMenuClick"
                        ></asc-report-table-cell>
                      </td>
                    </template>
                  </div>
                </tbody>
              </table>
            </td>
          </template>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {
  AscReportMenuClickMixin,
  AscStatusMethodsMixin,
} from '@/components/mixins'
import AscReportTableCell from './AscReportTableCell'
import { groupHeadings } from '@/utilities/reports'
import * as constants from '@/utilities/appConsts'
import AppFeatureFlags from '@/utilities/appFeatureFlags'

export default {
  name: 'AscReportColumnTable',

  components: {
    AscReportTableCell,
  },

  mixins: [AscReportMenuClickMixin, AscStatusMethodsMixin],

  props: {
    report: {
      type: Object,
      required: true,
    },
  },

  computed: {
    isProjectZeus() {
      return AppFeatureFlags.getProjectZeus()
    },
    hasNoHeadingsNoBorders() {
      return this.hasTableHeadings === false && this.report.borders === false
    },
    hasTableHeadings() {
      return (
        (this.report.headings !== undefined &&
          this.report.headings !== null &&
          this.report.headings.length > 0) ||
        (this.report.topHeadings !== undefined &&
          this.report.topHeadings !== null &&
          this.report.topHeadings.length > 0)
      )
    },
    hasTableSingleHeading() {
      return (
        this.report.headings !== undefined &&
        this.report.headings !== null &&
        this.report.headings.length > 0 &&
        (this.report.topHeadings === undefined ||
          this.report.topHeadings === null ||
          this.report.topHeadings.length === 0)
      )
    },
    hasTableDoubleHeadings() {
      return (
        this.report.headings !== undefined &&
        this.report.headings !== null &&
        this.report.headings.length > 0 &&
        this.report.topHeadings !== undefined &&
        this.report.topHeadings !== null &&
        this.report.topHeadings.length > 0
      )
    },
    hasTableData() {
      return this.report.values !== undefined && this.report.values.length > 0
    },
    groupedTopHeadings() {
      return groupHeadings(this.report.topHeadings)
    },
    groupedHeadings() {
      const ghs = groupHeadings(this.report.headings)
      return ghs
    },
  },

  methods: {
    isNoMarginsTableRow(row) {
      const items = row.filter(item => item.paddingRight && !item.container)
      return items.length > 0
    },
    isWarningAlert(cell) {
      if (cell && cell.status) {
        return (
          (cell.status === constants.STATUS_VALUES.lowWarning ||
            cell.status === constants.STATUS_VALUES.highWarning ||
            cell.status === constants.STATUS_VALUES.highLowWarning ||
            cell.status === constants.STATUS_VALUES.eventWarning) &&
          cell.alertEnabled === true
        )
      }
      return false
    },
    isCautionAlert(cell) {
      if (cell && cell.status) {
        return (
          (cell.status === constants.STATUS_VALUES.lowCaution ||
            cell.status === constants.STATUS_VALUES.highCaution ||
            cell.status === constants.STATUS_VALUES.highLowCaution ||
            cell.status === constants.STATUS_VALUES.eventCaution) &&
          cell.alertEnabled === true
        )
      }
      return false
    },
    isFaultMonitorAlert(cell) {
      return cell.status === constants.STATUS_VALUES.faultMonitor
    },
  },
}
</script>

<style lang="scss">
.asc-report-column-table {
  margin-bottom: 16px;

  .asc-report-column-table--no-borders {
    border-bottom: none !important;
    margin-top: 6px;

    tr {
      border-bottom: none !important;
    }
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    border-bottom: 1px solid $gray-300;

    tr {
      border-bottom: 1px solid $gray-300;
    }

    th {
      padding: 0.5em;
      border: 1px solid $primary-white;
      border-left: none;
    }

    td {
      text-align: left;
    }

    tr:nth-child(even) {
      background-color: $gray-0;
    }

    .table-cell--hover {
      &:hover {
        color: $primary-black;
        cursor: pointer;
      }
    }

    .table-cell--drill-ripple {
      background-position: center;
      transition: background 0.8s;
    }

    .table-cell--drill-ripple:hover {
      // prettier-ignore
      background: $gray-200 radial-gradient(circle, transparent 1%, $gray-200 1%) center/15000%;
    }

    .table-cell--drill-ripple:active {
      background-color: $gray-300;
      background-size: 100%;
      transition: background 0s;
    }

    .table-cell--drill-ripple-accent:hover {
      // prettier-ignore
      background: $accent-100 radial-gradient(circle, transparent 1%, $accent-100 1%) center/15000%;
    }

    .table-cell--drill-ripple-accent:active {
      background-color: $accent-200;
      background-size: 100%;
      transition: background 0s;
    }

    .table-cell--hover-warning {
      &:hover {
        color: $primary-black;
        cursor: pointer;
        text-decoration-color: $warning-base;
      }
    }

    .table-cell--warning-drill-ripple:hover {
      // prettier-ignore
      background: $warning-lighten1 radial-gradient(circle, transparent 1%, $warning-lighten1 1%) center/15000%;
    }

    .table-cell--warning-drill-ripple:active {
      background-color: $warning-lighten2;
      background-size: 100%;
      transition: background 0s;
    }

    .table-cell--hover-caution {
      &:hover {
        color: $primary-black;
        cursor: pointer;
        text-decoration-color: $caution-base;
      }
    }

    .table-cell--caution-drill-ripple:hover {
      // prettier-ignore
      background: $caution-lighten1 radial-gradient(circle, transparent 1%, $caution-lighten1 1%) center/15000%;
    }

    .table-cell--caution-drill-ripple:active {
      background-color: $caution-lighten1;
      background-size: 100%;
      transition: background 0s;
    }

    .table-cell--hover-fault-monitor {
      &:hover {
        color: $primary-black;
        cursor: pointer;
      }
    }

    .table-cell--fault-monitor-drill-ripple:hover {
      // prettier-ignore
      background: $gray-200 radial-gradient(circle, transparent 1%, $gray-200 1%) center/15000%;
    }

    .table-cell--fault-monitor-drill-ripple:active {
      background-color: $gray-300;
      background-size: 100%;
      transition: background 0s;
    }

    .table-cell--simple-text {
      padding: 0.5em;
      font-size: $font-size-sm;
      margin-top: 2px;
    }

    tbody th {
      background-color: $gray-300;
      font-weight: normal;
      font-size: $font-size-sm;
      border-right: none;
    }

    thead .top-header th {
      background-color: $gray-300;
      font-weight: normal;
      font-size: $font-size-sm;
      border-top: none;
    }

    thead .bottom-header th {
      background-color: $gray-300;
      font-weight: normal;
      font-size: $font-size-sm;
    }

    thead tr th:first-of-type {
      border-left: 1px solid $gray-300;
    }

    thead .top-header .empty-header {
      border: 0;
      background-color: $primary-white;
    }

    thead .bottom-header .empty-header {
      border: 0;
      background-color: $primary-white;
    }

    tbody tr:last-of-type th {
      border-bottom: 1px solid $gray-300;
    }

    tbody .table-subtotal {
      font-weight: 600;
      border: 0;
    }

    tbody .table-cell--center {
      text-align: center;
    }

    tbody .table-cell-no-borders {
      border: 0;
      padding: 0;

      .table-cell--simple-text {
        padding: 0;
      }
    }

    tbody .table-cell--padding-right {
      padding-right: 4px;
      vertical-align: middle;
    }

    .table-cell--md-font-size {
      font-size: $font-size-md;
    }
  }
}
</style>
